import React from 'react';
import { Col } from 'antd';
import ChartBarPercent from '../../graphs/ChartBarPercent';

import {
  Header,
  Content,
  RowContent,
  Graphs,
  GraphBar,
  Line,
  ColM,
  ColBar,
} from './styles'
import TextsHeader from '../../TextsHeader';
import { MODULE_COUNT } from '../../constants/examRecommendations';

// Função auxiliar para criar módulos vazios
const createEmptyModules = (count, simuladoNumber) => {
  return Array.from({ length: count }, (_, index) => ({
    acertos: 0,
    media_acertos: 0,
    media_total: 0,
    module: String(index + 1),
    nota_percentual: 0,
    porcentagem: 0,
    simulado: String(simuladoNumber),
    total: 0
  }));
};

// Função para extrair número do módulo do título CFA
const extractModuleFromTitle = (title) => {
  // Procura por padrões como "(M1 -", "(M2 -", "(M10 -" etc. no título
  const match = title.match(/\(M(\d+)\s*-/);
  if (match) {
    return match[1]; // Retorna o número do módulo (1, 2, 3, ..., 10)
  }
  return null;
};

// Função para extrair número do simulado do título CFA
const extractSimuladoFromTitle = (title) => {
  // Procura por "Simulado 1", "Simulado 3", "Simulado 5", etc.
  const match = title.match(/Simulado\s+(\d+)/);
  if (match) {
    return match[1]; // Retorna o número do simulado
  }
  return null;
};

// Função para processar dados do CFA
const processCFAData = (simulatedModule) => {
  const processedData = {};
  
  Object.keys(simulatedModule).forEach((key) => {
    const items = simulatedModule[key];
    
    // Agrupar por simulado baseado no título
    items.forEach((item) => {
      // Extrair número do simulado do título (ex: "Simulado 1", "Simulado 3")
      const simuladoNumber = extractSimuladoFromTitle(item.titulo);
      
      // Se não conseguiu extrair o simulado, pula este item
      if (!simuladoNumber) {
        return;
      }
      
      const simuladoKey = `simulado_${simuladoNumber}`;
      
      if (!processedData[simuladoKey]) {
        processedData[simuladoKey] = [];
      }
      
      // Extrair número do módulo do título (ex: "M1", "M4", "M10")
      const moduleNumber = extractModuleFromTitle(item.titulo);
      
      // Se for Simulado 1, tratar MANHÃ e TARDE como Prova 1 e Prova 2
      if (simuladoNumber === '1' && !moduleNumber) {
        // Verificar se é MANHÃ ou TARDE
        if (item.titulo.includes('MANHÃ')) {
          processedData[simuladoKey].push({
            ...item,
            module: '1', // Prova 1
            simulado: simuladoNumber,
            isProva: true
          });
        } else if (item.titulo.includes('TARDE')) {
          processedData[simuladoKey].push({
            ...item,
            module: '2', // Prova 2
            simulado: simuladoNumber,
            isProva: true
          });
        }
        return;
      }
      
      // Para outros simulados, só adiciona se tiver módulo
      if (!moduleNumber) {
        return;
      }
      
      // Adicionar item com o módulo extraído
      processedData[simuladoKey].push({
        ...item,
        module: moduleNumber,
        simulado: simuladoNumber,
        isProva: false
      });
    });
  });
  
  return processedData;
};

function SimulatedPerModules(props) {
  const data = props.data;
  const examType = props.examType || 'cfp'; // Default para CFP
  const moduleCount = examType === 'cfa' ? MODULE_COUNT.cfa : MODULE_COUNT.cfp;

  let simulatedModule = data?.simulated_by_module ?? [];
  
  // Se for CFA, processar os dados para extrair módulos dos títulos
  if (examType === 'cfa') {
    simulatedModule = processCFAData(simulatedModule);
  }
  
  // Descobrir quais simulados existem nos dados
  const availableSimulados = Object.keys(simulatedModule).filter(key => 
    simulatedModule[key] && simulatedModule[key].length > 0
  );
  
  // Se não houver simulados, usar os padrões
  let simuladosToUse = availableSimulados.length > 0 
    ? availableSimulados 
    : ['simulado_1', 'simulado_3', 'simulado_5'];
  
  // Ordenar simulados em ordem crescente (simulado_1, simulado_3, simulado_5, etc.)
  simuladosToUse = simuladosToUse.sort((a, b) => {
    const numA = parseInt(a.replace('simulado_', ''));
    const numB = parseInt(b.replace('simulado_', ''));
    return numA - numB;
  });
  
  // Criar estrutura de mock dinâmica baseada nos simulados disponíveis
  const mockSimulatedByModule = {};
  simuladosToUse.forEach((simuladoKey) => {
    const simuladoNumber = simuladoKey.replace('simulado_', '');
    
    // Se for CFA e Simulado 1, criar apenas 2 módulos (Prova 1 e Prova 2)
    if (examType === 'cfa' && simuladoNumber === '1') {
      mockSimulatedByModule[simuladoKey] = createEmptyModules(2, simuladoNumber);
    } else {
      mockSimulatedByModule[simuladoKey] = createEmptyModules(moduleCount, simuladoNumber);
    }
  });

  Object.keys(mockSimulatedByModule).forEach((simulado) => {
    const modulos = mockSimulatedByModule[simulado];
    modulos.forEach((modulo) => {
      if (!!simulatedModule[simulado] && simulatedModule[simulado].filter(simuladoModulo => simuladoModulo.module === modulo.module).length) {
        mockSimulatedByModule[simulado][modulo.module - 1] = simulatedModule[simulado].filter(simuladoModulo => simuladoModulo.module === modulo.module)[0];
      }
    })
  })

  let modules = [];

  const simulado = mockSimulatedByModule;

  return (
    <>
      <TextsHeader>
        <p>
          Veja no detalhe o seu desempenho em cada Simulado. Se você realizou mais de uma vez algum dos simulados, vamos mostrar a média das rodadas em que você praticou. Segue comigo:
        </p>
      </TextsHeader>
      {
        Object.entries(simulado)?.map(([simuladoKey, itens]) => {
          const simuladoNumber = simuladoKey.replace('simulado_', '');
          const isSimulado1CFA = examType === 'cfa' && simuladoNumber === '1';
          
          // Determinar número de módulos para este simulado específico
          const currentModuleCount = isSimulado1CFA ? 2 : moduleCount;
          const currentCompleteNumbers = Array.from({ length: currentModuleCount }, (_, i) => i);
          
          // Inicializar array de módulos dinamicamente
          modules = Array(currentModuleCount).fill(0);
          currentCompleteNumbers.map((_, x) => {
            if (Object.values(itens)[x] !== undefined) {
              modules[parseInt(Object.values(itens)[x].module) - 1] = Object.values(itens)[x];
            }
            return 0;
          });

          return (
            <React.Fragment key={`simulado-${simuladoKey}`}>
              <Header style={{marginTop: "50px"}}>
                <h1>{simuladoKey.replace('_', ' ').replace('s', 'S')}</h1>
              </Header>

              <Content>
                <RowContent style={{ gap: examType === 'cfa' ? '3vw' : '5.140vw' }}>
                  <Col xs={examType === 'cfa' ? 14 : 12}>
                    <Graphs style={{ 
                      gap: isSimulado1CFA ? '3vw' : (examType === 'cfa' ? '0.2vw' : '1vw'),
                      justifyContent: isSimulado1CFA ? 'center' : 'space-between',
                      height: examType === 'cfa' ? '26.161vw' : '26.161vw'
                    }}>
                      {modules?.map((item, idx) => {
                        const progress = item === 0 ? 0 : item?.porcentagem;
                        const moduleCode = item?.module ?? String(idx + 1);
                        
                        return (
                          <GraphBar 
                            key={`simulado-${simuladoKey}-graph-${moduleCode}`}
                            progress={progress}
                            isCFA={examType === 'cfa'}
                            isSimulado1={isSimulado1CFA}
                          >
                            <p>{progress.toFixed(0)}%</p>
                          </GraphBar>
                        )
                      })
                      }
                    </Graphs>
                    <Line />
                    <ColM
                      isCFA={examType === 'cfa'}
                      isSimulado1={isSimulado1CFA}
                      style={{
                        gap: isSimulado1CFA ? '3vw' : (examType === 'cfa' ? '0.2vw' : '1vw'),
                        justifyContent: isSimulado1CFA ? 'center' : 'space-between'
                      }}
                    >
                      {
                        currentCompleteNumbers.map((item => {
                          // Se for Simulado 1 do CFA, mostrar "Prova 1" e "Prova 2"
                          const label = isSimulado1CFA
                            ? `Prova ${item + 1}`
                            : `M${item + 1}`;

                          return <h3 key={item} style={{
                            fontSize: examType === 'cfa' ? '1.2vw' : '1.584vw',
                            lineHeight: examType === 'cfa' ? '1.5vw' : '1.936vw'
                          }}>{label}</h3>
                        }))
                      }
                    </ColM>
                  </Col>

                  <ColBar xs={examType === 'cfa' ? 9 : 10} style={{
                    gap: examType === 'cfa' ? '1.2vw' : '1.5vw',
                    justifyContent: examType === 'cfa' ? 'flex-start' : 'space-evenly'
                  }}>
                    {modules?.filter(item => item !== 0).map((item) => {
                      const barHeight = examType === 'cfa' ? '1.8vw' : '2.922vw';
                      const fontSize = examType === 'cfa' ? '0.85vw' : '1.232vw';
                      const lineHeight = examType === 'cfa' ? '1vw' : '1.514vw';
                      const moduleCode = item?.module ?? 'unknown';
                      const hitPercent = Number(item?.total) > 0
                        ? ((Number(item?.acertos) / Number(item?.total)) * 100).toFixed()
                        : '0';
                      
                      // Se for Simulado 1 do CFA, mostrar título completo
                      const displayText = isSimulado1CFA 
                        ? (item?.module === '1' ? 'Prova 1' : 'Prova 2')
                        : `Módulo ${item?.module}`;
                      
                      return (
                        <div key={`simulado-${simuladoKey}-bar-${moduleCode}`} style={{
                          width: '100%',
                          height: barHeight,
                          marginBottom: examType === 'cfa' ? '0.4vw' : '0'
                        }}>
                          <div style={{
                            width: '100%',
                            height: '100%',
                            fontSize: fontSize,
                            lineHeight: lineHeight
                          }}>
                            <ChartBarPercent
                              percentModulo={hitPercent}
                              modulo={displayText}
                              total={`${item?.acertos}/${item?.total} acertos`}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </ColBar>
                </RowContent>
              </Content>
            </React.Fragment>
          )
        })}

    </>

  );
};

export default SimulatedPerModules;
